import { createContext, useContext } from "react";

import {
  useDenominacionesSociales,
  useRepresentacionesGeograficas,
  useDepartamentos,
  useFormasDePago,
  useFrecuenciasDePago,
  useCargos,
  useGeneros,
  useDecretosLegislativos,
  useCamarasDeComercio,
  useActividadesEconomicas,
} from "app/hooks";
import { Loading } from "app/components/loading";
import { Error } from "app/components/error";

const ResourcesContext = createContext();

export function ResourcesProvider({ children }) {
  const denominacionesSociales = useDenominacionesSociales();
  const representacionesGeograficas = useRepresentacionesGeograficas();
  const departamentosDeHonduras = useDepartamentos();
  const decretosLegislativos = useDecretosLegislativos();
  const camarasDeComercio = useCamarasDeComercio();
  const generos = useGeneros();
  const cargos = useCargos();
  const actividadesEconomicas = useActividadesEconomicas();
  const formasDePago = useFormasDePago();
  const frecuenciasDePago = useFrecuenciasDePago();

  function getDepartamento(departamentoId) {
    const departamento = departamentosDeHonduras.data.results.find(
      (d) => d.departamento_id === departamentoId
    );

    return departamento;
  }

  function getDepartamentoByCiudadId(ciudadId) {
    const departamento = departamentosDeHonduras.data.results.find(
      (d) => !!d.ciudades.find((c) => c.ciudad_id === ciudadId)
    );

    return departamento;
  }

  function getCiudad(ciudadId) {
    const departamento = departamentosDeHonduras.data.results.find((d) =>
      d.ciudades.find((c) => c.ciudad_id === ciudadId)
    );

    return departamento.ciudades.find((c) => c.ciudad_id === ciudadId);
  }

  function getNombreDeCiudad(ciudadId) {
    const departamento = departamentosDeHonduras.data.results.find((d) =>
      d.ciudades.find((c) => c.ciudad_id === ciudadId)
    );

    const ciudad = departamento.ciudades.find((c) => c.ciudad_id === ciudadId);

    return `${ciudad.ciudad_nombre}, ${departamento.departamento_nombre}`;
  }

  function getCargos(cargosId) {
    return (
      <ul>
        {cargosId.map((cargoId) => {
          const cargo = cargos.data.results.find(
            (cargo) => cargo.cargo_id === cargoId
          );

          return <li key={cargoId}>{cargo.cargo_nombre}</li>;
        })}
      </ul>
    );
  }

  function getGenero(generoId) {
    const genero = generos.data.results.find(
      (genero) => genero.genero_id === generoId
    );

    return genero.genero_nombre;
  }

  function getActividadEconomica(actividadEconomicaId) {
    const actividad_economica = actividadesEconomicas.data.find(
      (actividad) => actividad.actividad_economica_id === actividadEconomicaId
    );

    return actividad_economica;
  }

  if (
    denominacionesSociales.isLoading ||
    representacionesGeograficas.isLoading ||
    departamentosDeHonduras.isLoading ||
    camarasDeComercio.isLoading ||
    decretosLegislativos.isLoading ||
    generos.isLoading ||
    cargos.isLoading ||
    actividadesEconomicas.isLoading ||
    formasDePago.isLoading ||
    frecuenciasDePago.isLoading
  ) {
    return (
      <Loading label="Cargando el formulario de solicitud de inscripción, por favor, espere..." />
    );
  }

  if (
    denominacionesSociales.isError ||
    representacionesGeograficas.isError ||
    departamentosDeHonduras.isError ||
    camarasDeComercio.isError ||
    decretosLegislativos.isError ||
    generos.isError ||
    cargos.isError ||
    actividadesEconomicas.isError ||
    formasDePago.isError ||
    frecuenciasDePago.isError
  ) {
    return <Error label="Error al cargar el formulario" />;
  }

  return (
    <ResourcesContext.Provider
      value={{
        denominacionesSociales: denominacionesSociales.data.results,
        representacionesGeograficas: representacionesGeograficas.data.results,
        departamentos: departamentosDeHonduras.data.results,
        getDepartamento,
        getDepartamentoByCiudadId,
        getCiudad,
        getNombreDeCiudad,
        camarasDeComercio: camarasDeComercio.data.results,
        decretosLegislativos: decretosLegislativos.data.results,
        generos: generos.data.results,
        getGenero,
        cargos: cargos.data.results,
        getCargos,
        actividadesEconomicas: actividadesEconomicas.data,
        getActividadEconomica,
        formasDePago: formasDePago.data.results,
        frecuenciasDePago: frecuenciasDePago.data.results,
      }}
    >
      {children}
    </ResourcesContext.Provider>
  );
}

export function useResourcesContext() {
  const resourcesContext = useContext(ResourcesContext);

  if (!resourcesContext) {
    throw new Error("ResourcesProvider is missing");
  }

  return resourcesContext;
}
